import { ChatOrderGuidesInfo } from '@choco-admin/types';
import { isValidCurrencyCode } from '@choco-admin/utils';

import { CsvIssue, OrderGuideUploadRecord, UploadGroup } from '../types';

import { recordToUploadItem } from './mapping';

export const MAX_ROWS_PER_GROUP = 500;
export const MAX_ITEMS_PER_ORDER_GUIDE = 2000;
export const MAX_CATEGORIES_PER_ORDER_GUIDE = 60;
export const MAX_CATEGORY_LENGTH = 30;
export const MAX_PAR_LENGTH = 18;
/** GraphQL Int is a signed 32-bit integer */
const MAX_PRICE_MINOR_UNITS = 2147483647;

export const groupKey = (chatId: string, orderGuideId: string): string =>
  `${chatId}::${orderGuideId}`;

/** Duplicate detection: catalog rows by product code, custom rows by name */
const duplicateKey = (record: OrderGuideUploadRecord): string =>
  record.productId
    ? `code:${record.productId}::${record.unit}`
    : `name:${record.productName.toLowerCase()}::${record.unit}`;

/**
 * Row-level validation implementing the Retool template rules.
 */
export const validateRecords = (
  records: OrderGuideUploadRecord[]
): CsvIssue[] => {
  const issues: CsvIssue[] = [];
  const error = (row: number, column: string | null, message: string) =>
    issues.push({ row, column, message, severity: 'error' });

  const seenPerGroup = new Map<string, Map<string, number>>();

  records.forEach((record) => {
    const row = record.rowNumber;

    if (!record.chatId) {
      error(row, 'chat_id', 'chat_id cannot be empty.');
    }
    if (!record.productName) {
      error(row, 'product_name', 'product_name cannot be empty.');
    }
    if (!record.unit) {
      error(
        row,
        'unit',
        'unit cannot be empty and has to exactly match the unit of the catalog.'
      );
    }
    if (record.category.length > MAX_CATEGORY_LENGTH) {
      error(
        row,
        'category',
        `category cannot exceed ${MAX_CATEGORY_LENGTH} characters (got ${record.category.length}).`
      );
    }
    if (record.par.length > MAX_PAR_LENGTH) {
      error(
        row,
        'par',
        `par cannot exceed ${MAX_PAR_LENGTH} characters (got ${record.par.length}).`
      );
    }
    if (record.price) {
      if (!/^\d+$/.test(record.price)) {
        error(
          row,
          'price',
          'price must be a whole number in cents — no "," or "." allowed.'
        );
      } else if (Number(record.price) > MAX_PRICE_MINOR_UNITS) {
        error(row, 'price', 'price is too large.');
      }
      if (!record.currency) {
        error(row, 'currency', 'currency is required when price is set.');
      }
    }
    if (record.currency && !isValidCurrencyCode(record.currency)) {
      error(
        row,
        'currency',
        `"${record.currency}" is not a valid ISO currency code (e.g. EUR, USD, GBP).`
      );
    }

    // Duplicates within the same (chat, order guide) target
    if (
      record.chatId &&
      record.unit &&
      (record.productId || record.productName)
    ) {
      const group = groupKey(record.chatId, record.orderGuideId);
      let seen = seenPerGroup.get(group);
      if (!seen) {
        seen = new Map<string, number>();
        seenPerGroup.set(group, seen);
      }
      const key = duplicateKey(record);
      const firstRow = seen.get(key);
      if (firstRow !== undefined) {
        error(
          row,
          null,
          `Duplicate product/unit combination — already defined for this order guide on row ${firstRow}.`
        );
      } else {
        seen.set(key, row);
      }
    }
  });

  return issues;
};

/**
 * Groups records by their (chat, order guide) target. Records without a
 * chat_id are excluded — row validation already reports them.
 */
export const groupRecords = (
  records: OrderGuideUploadRecord[]
): UploadGroup[] => {
  const groups = new Map<string, UploadGroup>();
  records.forEach((record) => {
    if (!record.chatId) return;
    const key = groupKey(record.chatId, record.orderGuideId);
    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        chatId: record.chatId,
        orderGuideId: record.orderGuideId || null,
        records: [],
        items: [],
        categoryCount: 0,
        chatInfo: null,
        targetGuide: null,
      };
      groups.set(key, group);
    }
    group.records.push(record);
    group.items.push(recordToUploadItem(record));
  });
  return Array.from(groups.values()).map((group) => {
    const categories = new Set(
      group.records
        .map((record) => record.category)
        .filter((category) => category !== '')
    );
    return { ...group, categoryCount: categories.size };
  });
};

const describeGuides = (chatInfo: ChatOrderGuidesInfo): string =>
  chatInfo.orderGuides
    .map((guide) => `${guide.id}${guide.name ? ` (${guide.name})` : ''}`)
    .join(', ');

/**
 * Key of the real order guide a group will write to, once resolved. Two raw
 * (chat, order_guide_id) groups can resolve to the same guide — e.g. one row
 * leaves order_guide_id empty on a chat that has exactly one guide, another
 * row on the same chat states that guide's id explicitly — and must be
 * merged before upload: left as separate groups, `upload()` would fire two
 * sequential orderGuideV2BatchUpdate calls at the same guide, and in Replace
 * mode the second call silently wipes out whatever the first just wrote.
 */
const resolvedTargetKey = (group: UploadGroup): string =>
  `${group.chatId}::${group.targetGuide?.id ?? '(new)'}`;

/**
 * Combines groups that resolved to the same real order guide. Groups whose
 * chat lookup failed (no chatInfo) are left as-is — each already carries its
 * own blocking issue and can't be uploaded regardless.
 */
export const mergeGroupsByResolvedTarget = (
  groups: UploadGroup[]
): UploadGroup[] => {
  const merged = new Map<string, UploadGroup>();

  groups.forEach((group) => {
    if (!group.chatInfo) {
      merged.set(group.key, group);
      return;
    }

    const targetKey = resolvedTargetKey(group);
    const existing = merged.get(targetKey);
    if (!existing) {
      merged.set(targetKey, {
        ...group,
        key: targetKey,
        orderGuideId: group.targetGuide?.id ?? null,
      });
      return;
    }

    const records = [...existing.records, ...group.records];
    const categories = new Set(
      records
        .map((record) => record.category)
        .filter((category) => category !== '')
    );
    merged.set(targetKey, {
      ...existing,
      records,
      items: [...existing.items, ...group.items],
      categoryCount: categories.size,
    });
  });

  return Array.from(merged.values());
};

/**
 * Group-level validation once chat lookups have completed. Returns issues
 * plus groups enriched with chat info and their resolved target guide, with
 * groups sharing a real target guide merged into one (see
 * `mergeGroupsByResolvedTarget`).
 */
export const validateGroups = (
  groups: UploadGroup[],
  chatInfoById: Map<string, ChatOrderGuidesInfo | null>
): { issues: CsvIssue[]; groups: UploadGroup[] } => {
  const issues: CsvIssue[] = [];

  const resolved = groups.map((group) => {
    const firstRow = group.records[0]?.rowNumber ?? null;
    const chatInfo = chatInfoById.get(group.chatId) ?? null;

    if (!chatInfo) {
      issues.push({
        row: firstRow,
        column: 'chat_id',
        message: `Chat "${group.chatId}" was not found in this environment.`,
        severity: 'error',
      });
      return { ...group, chatInfo: null, targetGuide: null };
    }

    let targetGuide = null;
    if (group.orderGuideId) {
      targetGuide =
        chatInfo.orderGuides.find((guide) => guide.id === group.orderGuideId) ??
        null;
      if (!targetGuide) {
        issues.push({
          row: firstRow,
          column: 'order_guide_id',
          message: `Order guide "${group.orderGuideId}" does not belong to chat "${group.chatId}". ${
            chatInfo.orderGuides.length > 0
              ? `Available order guides: ${describeGuides(chatInfo)}.`
              : 'This chat has no order guides yet — leave order_guide_id empty to create one.'
          }`,
          severity: 'error',
        });
      }
    } else if (chatInfo.orderGuides.length > 1) {
      issues.push({
        row: firstRow,
        column: 'order_guide_id',
        message:
          `Chat "${group.chatId}" has ${chatInfo.orderGuides.length} order guides — ` +
          `order_guide_id is required to pick one. Available: ${describeGuides(chatInfo)}.`,
        severity: 'error',
      });
    } else {
      targetGuide = chatInfo.orderGuides[0] ?? null;
    }

    return { ...group, chatInfo, targetGuide };
  });

  const merged = mergeGroupsByResolvedTarget(resolved);

  merged.forEach((group) => {
    const firstRow = group.records[0]?.rowNumber ?? null;

    if (group.records.length > MAX_ROWS_PER_GROUP) {
      issues.push({
        row: firstRow,
        column: null,
        message:
          `${group.records.length} products for one order guide exceed the limit of ` +
          `${MAX_ROWS_PER_GROUP} per upload — split them into separate uploads.`,
        severity: 'error',
      });
    }
    if (group.categoryCount > MAX_CATEGORIES_PER_ORDER_GUIDE) {
      issues.push({
        row: firstRow,
        column: 'category',
        message:
          `${group.categoryCount} distinct categories exceed the limit of ` +
          `${MAX_CATEGORIES_PER_ORDER_GUIDE} per order guide.`,
        severity: 'error',
      });
    }
    const existingItems = group.targetGuide?.itemsCount ?? 0;
    if (existingItems + group.records.length > MAX_ITEMS_PER_ORDER_GUIDE) {
      issues.push({
        row: firstRow,
        column: null,
        message:
          `This order guide already has ${existingItems} products; adding ${group.records.length} more ` +
          `may exceed the maximum of ${MAX_ITEMS_PER_ORDER_GUIDE}. The backend will reject the upload if it does.`,
        severity: 'warning',
      });
    }
  });

  return { issues, groups: merged };
};
